<template>
  <div class="container d-flex flex-column">
    <Logged-user />
    <div class="row loader" v-if="loading">
      <div class="col align-self-center text-center ">
        <square-loader />
      </div>
    </div>
    <div v-else>
      <div>
        <div class="row">
          <div class="board-area col-12 col-lg-8 col-xl-7">
            <div class="row mt-4 mb-4" v-if="gameEnded">
              <div class="col-12">
                <img src="/static/i_winners.svg" width="100%"/>
              </div>
            </div>
            <div class="row mt-4 mb-4" v-if="gameEnded">
              <div class="col-12">
                <FinalScores v-bind:scores="scores" v-bind:totalTop="totalTop"/>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div v-if="updating" class="updating">
                  <clip-loader />
                </div>
                <Board v-bind:board="board"
                v-bind:roundNumber="roundNumber"
                ></Board>
              </div>
            </div>
            <div class="draw-area row mt-2">
              <div class="col-8" v-if="gameStarted && !gameEnded">
                <GameDraw
                v-bind:draw="draw"
                v-bind:roundNumber="roundNumber">
                </GameDraw>
              </div>
              <div class="col-8" v-if="!gameStarted">
                La partie va bientôt commencer.
              </div>
              <div class="col-4 d-block d-md-none">
                <RoundTimer v-bind:endDate="endDateTime" v-if="!gameEnded"></RoundTimer>
              </div>
            </div>
            <div class="row mt-4 d-none d-md-flex" v-if="gameStarted && definition">
              <div class="col-12">
                <Definition
                  v-bind:word="word"
                  v-bind:definition="definition"/>
              </div>
            </div>
          </div>
          <div class="info-area col-12 col-lg-4 col-xl-5">
            <div class="row">
              <div class="col-6">Partie: {{game.gameName}}</div>
              <div class="col text-right" v-if="gameEnded">
                <router-link to="/lobby" class="back">
                  Retour au salon
                </router-link>
              </div>
              <div class="col text-right" v-else>
                <router-link to="/lobby" class="back">
                  Retour au salon
                </router-link><br />
                <a class="back" href="`/games/${this.game.id}`" v-on:click="joinGame">
                  Rejoindre la partie
                </a>
              </div>
            </div>
            <div class="d-none d-md-flex row align-items-center">
              <div class="col-3">
                <RoundTimer v-bind:endDate="endDateTime" v-if="!gameEnded"></RoundTimer>
              </div>
              <div class="col-4" v-if="roundNumber > 0">
                Tour: {{roundNumber}}
              </div>
            </div>
            <div>
              <GameScores v-bind:scores="scores"
              v-bind:totalTop="totalTop"
              v-bind:lastTopWord="lastTopWord"
              v-bind:lastTopWordPoints="lastTopWordPoints"></GameScores>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-auto">
      <div class="mt-4">
      <bottom-nav />
      </div>
    </div>
  </div>
</template>

<script>
import iam from '../../iam';
import SquareLoader from '../SquareLoader';
import BottomNav from '../BottomNav';
import ClipLoader from '../ClipLoader';
import Board from './Board';
import GameDraw from './GameDraw';
import LoggedUser from '../LoggedUser';
import BoardUtils from './BoardUtils';
import RoundTimer from './RoundTimer';
import GameScores from './GameScores';
import FinalScores from './FinalScores';
import Definition from './Definition';
import Deck from './Deck';

const padZero = n => ((n < 10) ? `0${n}` : n);

export default {
  name: 'GameView',
  props: {
    activeGameId: {
      type: String,
      required: true,
    },
  },
  components: {
    SquareLoader,
    ClipLoader,
    Board,
    GameDraw,
    LoggedUser,
    BottomNav,
    RoundTimer,
    GameScores,
    FinalScores,
    Definition,
    Deck,
  },
  data() {
    return {
      loading: true,
      updating: false,
      username: iam.getConnectedUser().username,
      game: undefined,
      board: undefined,
      draw: undefined,
      scores: undefined,
      totalTop: 0,
      lastTopWordPoints: 0,
      lastTopWord: undefined,
      boardValid: undefined,
      bestRoundPlayed: undefined,
      roundEndDate: undefined,
      gameStarted: false,
      gameEnded: false,
      timeout: undefined,
      roundNumber: undefined,
      deck: undefined,
      horizontal: true,
      word: undefined,
      definition: undefined,
    };
  },
  computed: {
    endDateTime() {
      return this.roundEndDate
        ? this.roundEndDate.getTime()
        : new Date(this.game.startDate).getTime();
    },
  },
  async created() {
    try {
      this.game = await this.findGameById(this.activeGameId);
    } catch (error) {
      this.$router.push(`/ended-games/${this.activeGameId}`);
    }
    await this.loadRound();
    this.loading = false;
  },
  beforeDestroy() {
    clearTimeout(this.timeout);
  },
  methods: {
    async sleep(time) {
      return new Promise(resolve => setTimeout(resolve, time));
    },
    async loadRound() {
      this.updating = true;
      clearTimeout(this.timeout);
      await this.sleep(200);
      this.bestRoundPlayed = undefined;
      return this.findGameById(this.activeGameId).then((game) => {
        if (game.roundNumber > game.nbRounds) { // game ended
          this.gameEnded = true;
          this.timeout = setTimeout(() => this.$router.push(`/ended-games/${this.activeGameId}`), 120000);
        } else if (game.timeToNextRound) {
          this.timeout = setTimeout(() => this.loadRound(), game.timeToNextRound * 1000);
        }
        if (game.roundNumber > 0) {
          this.gameStarted = true;
        }
        if (game.rounds) {
          this.updateData(game);
        }
        this.updating = false;
      });
    },
    updateData(game) {
      this.roundEndDate = new Date(new Date().getTime() + (game.timeToNextRound * 1000));
      this.roundNumber = game.roundNumber;
      this.totalTop = game.rounds.slice(0, -1).reduce((acc, r) => acc + r.points, 0);
      const lastRound = game.roundNumber > 1 ? game.rounds.slice(-2, -1).pop() : undefined;
      this.lastTopWord = lastRound ? lastRound.word : undefined;
      this.lastTopWordPoints = lastRound ? lastRound.points : 0;
      this.scores = game.scores;
      this.board = BoardUtils.buildBoard(game.rounds.slice(0, -1));
      this.draw = BoardUtils.buildDraw(game.rounds.slice(-1)[0]);
      if (this.gameEnded) {
        this.roundNumber = this.game.nbRounds;
        const latestRound = game.rounds.slice(-1)[0];
        this.lastTopWord = latestRound.word;
        this.lastTopWordPoints = latestRound.points;
        this.board = BoardUtils.buildBoard(game.rounds);
        this.draw = [];
      }
      if (this.lastTopWord) {
        this.displayDefinition(this.lastTopWord.split(' ')[1]);
      }
    },
    convertDate(timestamp) {
      const h = padZero(new Date(Date.parse(timestamp)).getHours());
      const m = padZero(new Date(Date.parse(timestamp)).getMinutes());
      return `${h}h${m}`;
    },
    findGameById(activeGameId) {
      return this.$http.get(`${process.env.LCT_API_URL}/active-games/${activeGameId}`)
        .then(response => response.data);
    },
    lobby() {
      this.$router.push('/lobby');
    },
    displayDefinition(word) {
      const w = word.replace('(', '').replace(')', '').toUpperCase();
      this.$http.get(`${process.env.LCT_API_URL}/definitions/${w}`)
        .then((response) => {
          const def = response.data.result;
          if (def) {
            this.definition = def.split(';');
          }
          this.word = w;
        });
    },
    joinGame(event) {
      event.preventDefault();
      this.$http.post(`${process.env.LCT_API_URL}/game-players`, {
        username: iam.getConnectedUser().username,
        picture: iam.getConnectedUser().picture,
        activeGameId: this.game.id,
      }).then((response) => {
        sessionStorage.setItem(`${this.game.id}`, `${response.data.id}`);
        this.$router.push(`/games/${this.game.id}`);
      });
    },
  },
};
</script>

<style scoped>
div.loader {
  min-height: 100vh;
}
.updating{
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255,255,255,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.back{
  color: #CE0F69;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/games/GameView.vue